/**
 * RoleCandidatesPanel.jsx — Phase 1.2 minimal candidate-upload surface.
 *
 * Mounts inside the Role Detail page. Surfaces:
 *   • Candidate list scoped to the role (read from
 *     /api/employer/v1/roles/:id/candidates).
 *   • Evaluation + verdict-override workflow.
 *
 * RC 1.62 Phase B (2026-02-28): the single + bulk upload affordances
 * that previously lived in this panel were extracted into
 * `RoleUploadPanel.jsx` and mounted under the new "Upload Dossiers"
 * parent tab. This panel still accepts a `canUpload` prop for
 * backward-compatibility with any external callers, but the prop is
 * now a no-op — the upload UX never renders here. The component
 * continues to refresh on `refreshToken` bumps so the Role Detail
 * page can signal "freshly uploaded — reload" from the Upload tab.
 *
 * STRICT OPERATIONAL HONESTY:
 *   - No scoring. No ranking. No verdict. No fake activity.
 *   - `evaluation_status === 'not_evaluated'` is shown verbatim as
 *     "Not evaluated" with the literal next-action label
 *     "Evaluation coming in next phase".
 *   - Duplicate hash inside the same role surfaces a toast +
 *     scrolls to the existing row.
 *   - Parse failures show the machine code, never invented copy.
 *
 * Out of scope (deferred to later slices):
 *   - Candidate detail view.
 *   - PII display beyond filename.
 *   - Credit deduction.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Loader2, FileText, RefreshCw, AlertTriangle, ChevronDown,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import { apiFetch } from '@/lib/api';
// RC 1.62 Phase B — single + bulk upload affordances moved to
// `RoleUploadPanel.jsx` and mounted inside the "Upload Dossiers"
// parent tab; imports retired here.
// RC EMPLOYER.OVERAGE.REMOVAL — EvalChargeConfirmDialog is no longer
// imported here. Beyond-cap is a hard 402 with an institutional
// message; no acceptance dialog is shown to the operator.
import CandidateVerdictExplainer from './CandidateVerdictExplainer';
import RoleWorkflowSummary from './RoleWorkflowSummary';
import GovernancePackPanel from './GovernancePackPanel';
import IntegrityRow from './IntegrityRow';
import { Checkbox } from '@/components/ui/checkbox';

// RC 1.62 M146 — imported-candidate reviewer routing.
import RouteToReviewersButton from './RouteToReviewersButton';
import { listReviewerAssignments } from '@/lib/employerReviewerRoster';

// RC EMPLOYER.WORKFLOW.P0 (Founder-approved 2026-02-27) — closed
// filter vocabulary for the Candidates panel. Two visually distinct
// groups share ONE filter state (mutually exclusive selection).
//
// Group A — DOSSIERS & ADMISSIBILITY
//   ALL is the pure corpus lens (uploaded, non-erased) and carries
//   NO workflow qualification. AWAITING / EXCLUDED / ELIGIBLE are
//   the admissibility workflow views, keyed on the frozen Rev 4.4
//   six-state classifier (`integrity_classification`).
//
// Group B — NDOR EVALUATION RESULTS
//   SHORTLIST / REVIEW / REJECT are the NDOR completed-assessment
//   result views. They resolve EXCLUSIVELY from the authoritative
//   `emp_evaluations.governance_verdict` column (backend projection)
//   — never from `emp_candidates.governance_status`.
//
// ELIGIBLE FOR ASSESSMENT is the sole first-assessment action lane.
// Result views expose no first-assessment / bulk-assessment controls.
const FILTER_GROUP_ADMISSIBILITY = [
  { key: 'all',       label: 'All candidates',       lane: 'corpus' },
  { key: 'awaiting',  label: 'Awaiting integrity',   lane: 'admissibility' },
  { key: 'excluded',  label: 'Excluded',             lane: 'admissibility' },
  { key: 'eligible',  label: 'Eligible for assessment', lane: 'admissibility' },
];
const FILTER_GROUP_NDOR = [
  { key: 'shortlist', label: 'Shortlist',            lane: 'ndor' },
  { key: 'review',    label: 'Review',               lane: 'ndor' },
  { key: 'reject',    label: 'Reject',               lane: 'ndor' },
];
const NDOR_KEYS = new Set(['shortlist', 'review', 'reject']);

// Manual governance-override targets (PATCH /candidates/{id}/governance).
// These are HUMAN decisions, orthogonal to NDOR recommendations.
const OVERRIDE_OPTIONS = [
  { key: 'shortlisted', label: 'Move to shortlist' },
  { key: 'review',      label: 'Move to review' },
  { key: 'rejected',    label: 'Reject' },
  { key: 'open',        label: 'Reset to open' },
];


// NDOR recommendation badge — filled, high-contrast. Reads
// EXCLUSIVELY from `emp_evaluations.governance_verdict` (closed
// enum: shortlist | review | reject).
function ndorBadge(governanceVerdict) {
  if (!governanceVerdict) return null;
  const map = {
    shortlist: { cls: 'border-emerald-500/60 bg-emerald-500/20 text-emerald-100', label: 'Shortlist' },
    review:    { cls: 'border-amber-500/60 bg-amber-500/20 text-amber-100',       label: 'Review' },
    reject:    { cls: 'border-rose-500/60 bg-rose-500/20 text-rose-100',          label: 'Reject' },
  };
  const cfg = map[governanceVerdict];
  if (!cfg) return null;
  return (
    <Badge variant="outline" className={`${cfg.cls} text-[10px] uppercase tracking-wider`}
           data-testid="role-candidate-ndor-badge"
           data-verdict={governanceVerdict}>
      NDOR · {cfg.label}
    </Badge>
  );
}


// Employer decision badge — outline, secondary. Reads from
// `emp_candidates.governance_status`. Distinct visual language from
// the NDOR badge above.
function decisionBadge(governanceStatus) {
  const map = {
    shortlisted: { cls: 'border-emerald-500/40 text-emerald-300', label: 'Shortlisted' },
    review:      { cls: 'border-amber-500/40 text-amber-300',     label: 'Under review' },
    rejected:    { cls: 'border-rose-500/40 text-rose-300',       label: 'Rejected' },
    open:        { cls: 'border-ndor-edge text-ndor-ink-muted',   label: 'Awaiting decision' },
  };
  const cfg = map[governanceStatus] || map.open;
  return (
    <Badge variant="outline" className={`${cfg.cls} text-[10px] uppercase tracking-wider`}
           data-testid="role-candidate-decision-badge"
           data-decision={governanceStatus || 'open'}>
      {cfg.label}
    </Badge>
  );
}


function statusBadge(status) {
  const map = {
    parsed:       { cls: 'border-emerald-500/40 text-emerald-300', label: 'Parsed' },
    parsing:      { cls: 'border-amber-500/40 text-amber-300',     label: 'Parsing' },
    pending:      { cls: 'border-ndor-edge text-ndor-ink-muted',   label: 'Pending' },
    parse_failed: { cls: 'border-rose-500/40 text-rose-300',       label: 'Parse failed' },
  };
  const cfg = map[status] || map.pending;
  return (
    <Badge variant="outline" className={`${cfg.cls} text-[10px] uppercase tracking-wider`}>
      {cfg.label}
    </Badge>
  );
}


function formatBytes(n) {
  if (!n) return '—';
  if (n < 1024)         return `${n} B`;
  if (n < 1024 * 1024)  return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}


function relativeTime(iso) {
  if (!iso) return '';
  const diff = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (diff < 60)    return 'just now';
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}


export default function RoleCandidatesPanel({
  roleId, roleTitle, role = null, canUpload = true, refreshToken = 0,
  // WP8 §H.2/§H.5 — accept an externally-requested integrity filter
  // (typically pushed by the RoleBulkUploadPanel "Review flagged
  // dossiers" CTA or the BulkEvalConfirmDialog "Review dossiers
  // awaiting decision" CTA). Setting this to a non-null value
  // activates the corresponding integrity chip on mount / on change.
  integrityFilterRequest = null,
  onIntegrityFilterApplied = null,
}) {
  // RC 1.62 Phase B — `canUpload` is retained for backward-compat
  // with any external caller but is now a no-op. Single + bulk
  // upload affordances live in `RoleUploadPanel` inside the
  // "Upload Dossiers" parent tab on the Role Detail page.
  void canUpload;
  // RC 1.62 Role Lifecycle Gate Hardening (2026-05-30) — evaluation
  // affordances must be disabled when the role is not in `active`.
  // The backend rejects with 409 ROLE_NOT_OPEN regardless; this
  // keeps the UI honest.
  const _roleStatus = role?.status || '';
  const roleLifecycleBlocksEvaluate = !(
    _roleStatus === 'active' || _roleStatus === 'live'
  );
  const [items, setItems]     = useState(null);
  // RC EMPLOYER.WORKFLOW.P0 — top-level reconciliation envelope
  // returned by the list endpoint. Advisory metadata only; drives the
  // Workflow Summary counts and the invariant-violation banner.
  const [reconciliation, setReconciliation] = useState(null);
  const [error, setError]     = useState(null);
  const [evalBusy, setEvalBusy] = useState({}); // { [candidate_id]: true }
  // RC EMPLOYER.OVERAGE.REMOVAL — chargeDialog state removed.
  // Slice β — per-row verdict explainer expander state.
  const [expanded, setExpanded] = useState({}); // { [candidate_id]: true }
  // RC EMPLOYER.WORKFLOW.P0 — single, mutually-exclusive filter state
  // shared by both chip groups (admissibility + NDOR results). One of:
  //   'all' | 'awaiting' | 'excluded' | 'eligible'
  //   | 'shortlist' | 'review' | 'reject'
  const [activeFilter, setActiveFilter] = useState('all');
  // Legacy-compat: legacy `integrityFilterRequest` prop pushes an
  // external chip activation (currently only 'awaiting_disposition'
  // and 'eligible' from parent CTAs). Map onto the new keys.
  useEffect(() => {
    if (!integrityFilterRequest) return;
    const mapped =
      integrityFilterRequest === 'awaiting_disposition' ? 'awaiting'
      : integrityFilterRequest === 'excluded'           ? 'excluded'
      : integrityFilterRequest === 'eligible'           ? 'eligible'
      : null;
    if (mapped) setActiveFilter(mapped);
    if (typeof onIntegrityFilterApplied === 'function') {
      onIntegrityFilterApplied();
    }
  }, [integrityFilterRequest, onIntegrityFilterApplied]);
  // RC EMPLOYER.WORKFLOW.P0 — per-row override busy state.
  const [overrideBusy, setOverrideBusy] = useState({}); // {[candidate_id]: true}
  // Phase D F3 — divergence-rationale modal state (Founder-authored
  // decision surface reason + note + character counter). Opens
  // whenever the operator picks a non-'open' override; backend
  // still authoritative on whether the note is actually required.
  const [decisionModal, setDecisionModal] = useState(null);
  // shape: { candidateId, target, latestEvaluationId, ndorVerdict }
  const [decisionReason, setDecisionReason] = useState('ndor_recommendation_accepted');
  const [decisionNote, setDecisionNote] = useState('');
  const [decisionSaving, setDecisionSaving] = useState(false);
  // RC EMPLOYER.WORKFLOW.P0 — Re-evaluate confirmation modal state.
  // Result views (SHORTLIST/REVIEW/REJECT) permit Re-evaluate only as
  // an explicit, confirmed, individually-scoped action per Founder
  // doctrine v2 §Correction 3. Previous authoritative result remains
  // displayed while the new attempt is running or if it fails.
  const [reevalTarget, setReevalTarget] = useState(null); // {id, filename} | null
  // Feb 2026 workflow correction — Eligible-view selection state.
  // Only meaningful when the Eligible chip is active. Empty otherwise.
  const [selected, setSelected] = useState(() => new Set());
  const [bulkEvalBusy, setBulkEvalBusy] = useState(false);
  // Whenever the filter changes away from Eligible or the list
  // reloads with a candidate absent, drop stale selections.
  useEffect(() => {
    if (activeFilter !== 'eligible') {
      if (selected.size > 0) setSelected(new Set());
    }
  }, [activeFilter, selected.size]);
  // RC ENTITLEMENT.INTEGRITY.FIX — canonical monthly-cap usage state.
  const [usage, setUsage] = useState(null);

  // RC 1.62 M146 — reviewer routing context for imported candidates.
  // Mirrors the same loader used in `RoleSurfacedCandidatesPanel.jsx`.
  // accountId comes from `role?.org_id` (fast path) or /me (fallback).
  // activeReviewerCount counts active assignments for THIS role —
  // the routing affordance disables when zero.
  const [accountId, setAccountId] = useState(role?.org_id || null);
  const [activeReviewerCount, setActiveReviewerCount] = useState(0);
  const [memberRole, setMemberRole] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let aid = role?.org_id || null;
        let mrole = null;
        if (!aid) {
          const me = await apiFetch('/api/employer/v1/me');
          aid = me?.organisation?.id ?? me?.account_id ?? null;
          mrole = me?.member?.role ?? null;
        } else {
          try {
            const me = await apiFetch('/api/employer/v1/me');
            mrole = me?.member?.role ?? null;
          } catch { mrole = null; }
        }
        if (cancelled) return;
        setAccountId(aid);
        setMemberRole(mrole);
        if (!aid) { setActiveReviewerCount(0); return; }
        const assignments = await listReviewerAssignments(aid);
        const n = (assignments || []).filter(
          (a) => a.role_id === roleId && a.assignment_status === 'active',
        ).length;
        if (!cancelled) setActiveReviewerCount(n);
      } catch {
        if (!cancelled) setActiveReviewerCount(0);
      }
    })();
    return () => { cancelled = true; };
  }, [roleId, role?.org_id, refreshToken]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await apiFetch(`/api/employer/v1/roles/${roleId}/candidates`);
      setItems(data?.items || []);
      setReconciliation(data?.reconciliation || null);
    } catch (err) {
      setError(err?.message || 'Failed to load candidates.');
      setItems([]);
      setReconciliation(null);
    }
  }, [roleId]);

  // RC ENTITLEMENT.INTEGRITY.FIX — monthly-usage hydration. Reuses
  // the same /usage endpoint as RoleUsageStrip so both surfaces
  // converge on the same authoritative state on every refresh.
  const loadUsage = useCallback(async () => {
    try {
      const u = await apiFetch(`/api/employer/v1/roles/${roleId}/usage`);
      setUsage(u || null);
    } catch {
      setUsage(null);
    }
  }, [roleId]);

  useEffect(() => { if (roleId) load(); }, [roleId, load]);
  useEffect(() => { if (roleId) loadUsage(); }, [roleId, loadUsage]);
  // Slice β — refetch when parent signals a bulk job reached terminal.
  useEffect(() => { if (roleId && refreshToken > 0) { load(); loadUsage(); } }, [refreshToken, roleId, load, loadUsage]);

  const onEvaluate = useCallback(async (candidateId, force = false) => {
    setEvalBusy((p) => ({ ...p, [candidateId]: true }));
    try {
      // RC EMPLOYER.OVERAGE.REMOVAL (2026-02-27) — no overage path.
      // The preview is advisory only; we no longer branch to an
      // acceptance dialog when classification would be "overage".
      // The backend blocks beyond-cap requests with 402; this client
      // simply lets the request fly and surfaces the institutional
      // limit-reached message verbatim if it comes back.
      const body = await apiFetch(
        `/api/employer/v1/candidates/${candidateId}/evaluate`,
        { method: 'POST', body: JSON.stringify({ force }) },
      );
      if (body?.cached) {
        toast.message('Returned cached evaluation. Use Re-evaluate to refresh.');
      } else if (body?.governance_verdict) {
        toast.success(`Evaluated: ${body.governance_verdict}`);
      } else {
        toast.message('Evaluation submitted.');
      }
      await load();
      await loadUsage();
    } catch (err) {
      const detail = err?.data?.detail || err?.data || {};
      const code = detail.code || err?.code || 'EVALUATE_FAILED';
      const msg  = detail.message || detail.error_code || err?.message || 'Evaluation failed.';
      // Cap-reached → unified institutional message returned by backend.
      if (code === 'EVAL_BUDGET_EXHAUSTED') {
        toast.error(detail.message || 'Monthly evaluation allowance reached. Upgrade your workspace or wait until reset.');
        await loadUsage();
      } else {
        toast.error(`${code}: ${msg}`);
      }
    } finally {
      setEvalBusy((p) => { const n = { ...p }; delete n[candidateId]; return n; });
    }
  }, [load, loadUsage]);

  // RC EMPLOYER.OVERAGE.REMOVAL — `onEvaluateConfirmed` removed.
  // There is no overage acceptance flow; the evaluate handler above
  // is the only path. Beyond-cap → unified 402 → unified toast.

  // RC 1.62 Phase B — `onPickFile` + `onFile` (single dossier upload
  // handlers) moved into `RoleUploadPanel.jsx` along with the JSX
  // mode toggle, file input, bulk panel mount, and MAX_BYTES /
  // ACCEPTED_EXT constants. Backend contracts unchanged.

  // RC EMPLOYER.WORKFLOW.P0 — manual verdict override.
  // Phase D F3 (2026-02): non-'open' overrides open a decision surface
  // that captures the reason enum, an optional/required note, and
  // exposes a live 100-char counter. The backend re-derives divergence
  // server-side against the authoritative NDOR verdict and returns
  // 422 RATIONALE_TOO_SHORT if the note is short on a material
  // divergence. This modal keeps the operator ahead of that failure.
  const openDecisionSurface = useCallback((candidate, target) => {
    // Look up NDOR verdict from the current row payload.
    const ndor = candidate?.latest_evaluation?.governance_verdict
              || candidate?.governance_verdict
              || null;
    const latestEvaluationId = candidate?.latest_evaluation?.id
                            || candidate?.evaluation_id
                            || candidate?.latest_evaluation_id
                            || null;
    setDecisionReason('ndor_recommendation_accepted');
    setDecisionNote('');
    setDecisionModal({
      candidateId: candidate.id,
      candidateName: candidate?.anon_id || candidate?.display_name || candidate.id,
      target,
      latestEvaluationId,
      ndorVerdict: ndor,
    });
  }, []);

  // Divergence detection — mirrors backend Phase D F3 §11.B C1.3C.
  const isMaterialDivergence = useCallback((ndor, target) => {
    if (!ndor || target === 'open') return false;
    const nonDivergent = {
      shortlist: new Set(['shortlisted', 'review', 'open']),
      review:    new Set(['review', 'shortlisted', 'rejected', 'open']),
      reject:    new Set(['rejected', 'review', 'open']),
    };
    const allowed = nonDivergent[ndor] || new Set();
    return !allowed.has(target);
  }, []);

  const submitDecision = useCallback(async () => {
    if (!decisionModal) return;
    const { candidateId, target, latestEvaluationId, ndorVerdict } = decisionModal;
    const divergent = isMaterialDivergence(ndorVerdict, target);
    if (divergent && decisionNote.trim().length < 100) {
      toast.error('Divergent decisions require a rationale of at least 100 characters.');
      return;
    }
    setDecisionSaving(true);
    setOverrideBusy((p) => ({ ...p, [candidateId]: true }));
    try {
      const body = await apiFetch(
        `/api/employer/v1/candidates/${candidateId}/governance`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            governance_status:         target,
            decision_reason:           decisionReason,
            decision_note:             decisionNote,
            evaluation_id_at_decision: latestEvaluationId,
          }),
        },
      );
      if (body?.changed === false) {
        toast.message('Already at that verdict.');
      } else if (body?.is_material_divergence) {
        toast.success('Divergent decision recorded with rationale.');
      } else {
        toast.success(`Decision recorded: ${target}.`);
      }
      setDecisionModal(null);
      setDecisionNote('');
      await load();
    } catch (err) {
      const detail = err?.data?.detail || err?.data || {};
      const code = detail.code || err?.code || 'DECISION_FAILED';
      const msg  = detail.message || err?.message || 'Decision failed.';
      toast.error(`${code}: ${msg}`);
    } finally {
      setDecisionSaving(false);
      setOverrideBusy((p) => { const n = { ...p }; delete n[candidateId]; return n; });
    }
  }, [decisionModal, decisionReason, decisionNote, isMaterialDivergence, load]);

  // Legacy 'open' reset path — no decision surface, no rationale.
  const onOverrideVerdict = useCallback(async (candidateId, target) => {
    if (target !== 'open') {
      // Any non-'open' path routes through the decision surface.
      // Caller should have invoked openDecisionSurface(); retained
      // here for tests / legacy call sites that still pass a target.
      const row = (items || []).find((c) => c.id === candidateId) || { id: candidateId };
      openDecisionSurface(row, target);
      return;
    }
    setOverrideBusy((p) => ({ ...p, [candidateId]: true }));
    try {
      const body = await apiFetch(
        `/api/employer/v1/candidates/${candidateId}/governance`,
        {
          method: 'PATCH',
          body: JSON.stringify({ governance_status: 'open' }),
        },
      );
      if (body?.changed === false) {
        toast.message('Already at that verdict.');
      } else {
        toast.success('Decision reset to open.');
      }
      await load();
    } catch (err) {
      const detail = err?.data?.detail || err?.data || {};
      const code = detail.code || err?.code || 'OVERRIDE_FAILED';
      const msg  = detail.message || err?.message || 'Override failed.';
      toast.error(`${code}: ${msg}`);
    } finally {
      setOverrideBusy((p) => { const n = { ...p }; delete n[candidateId]; return n; });
    }
  }, [items, load, openDecisionSurface]);

  // Feb 2026 workflow correction — bulk evaluation from the Eligible
  // view. Reuses the existing WP6-gated evaluate-bulk/dispatch
  // authority; passes an explicit `candidate_ids` list so the caller
  // intent is auditable and NDORFlow never silently narrows the
  // request. WP6 will still 422 if any candidate has drifted to an
  // ineligible state between selection and dispatch (fail-loud).
  const onBulkEvaluate = useCallback(async () => {
    if (selected.size === 0) return;
    setBulkEvalBusy(true);
    try {
      const ids = Array.from(selected);
      // Backend requires an Idempotency-Key header on all
      // bulk-dispatch calls to make retries safe under the
      // emp_evaluation_charges.evaluation_id UNIQUE backstop.
      const idem = `eligible-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const body = await apiFetch(
        `/api/employer/v1/roles/${roleId}/evaluate-bulk/dispatch`,
        {
          method: 'POST',
          headers: { 'Idempotency-Key': idem },
          body: JSON.stringify({ candidate_ids: ids }),
        },
      );
      const dispatched = body?.dispatched_count ?? body?.dispatched ?? ids.length;
      toast.success(`Evaluation dispatched for ${dispatched} candidate${dispatched === 1 ? '' : 's'}.`);
      setSelected(new Set());
      await load();
      await loadUsage();
    } catch (err) {
      const detail = err?.data?.detail || err?.data || {};
      const code = detail.code || err?.code || 'BULK_DISPATCH_FAILED';
      const msg  = detail.message || err?.message || 'Bulk evaluation failed.';
      if (code === 'EVAL_BUDGET_EXHAUSTED') {
        toast.error(msg);
        await loadUsage();
      } else if (code === 'INTEGRITY_DISPOSITION_REQUIRED') {
        const parts = [];
        if (Array.isArray(detail.unresolved) && detail.unresolved.length) {
          parts.push(`${detail.unresolved.length} awaiting decision`);
        }
        if (Array.isArray(detail.excluded) && detail.excluded.length) {
          parts.push(`${detail.excluded.length} excluded`);
        }
        if (Array.isArray(detail.missing_check) && detail.missing_check.length) {
          parts.push(`${detail.missing_check.length} missing integrity check`);
        }
        toast.error(`Cannot dispatch: ${parts.join(', ')}. Refresh and re-select.`);
        await load();
      } else {
        toast.error(`${code}: ${msg}`);
      }
    } finally {
      setBulkEvalBusy(false);
    }
  }, [selected, roleId, load, loadUsage]);

  // RC EMPLOYER.WORKFLOW.P0 — single mutually-exclusive filter driving
  // both chip groups (admissibility + NDOR results). Reads:
  //   ALL      : erased_at IS NULL (corpus lens; no workflow qualification)
  //   AWAITING : integrity_classification ∈ {unresolved, missing_check}
  //   EXCLUDED : integrity_classification = 'excluded'
  //   ELIGIBLE : integrity_classification ∈ {clear, included,
  //              grandfathered_legacy}
  //   SHORTLIST/REVIEW/REJECT: evaluation_status='complete'
  //              AND governance_verdict = chip AND
  //              authoritative_evaluation_invalid = false
  //
  // NDOR chips ALWAYS resolve from the backend-projected
  // `governance_verdict` (from `emp_evaluations`), never from
  // `governance_status` (which is the human-decision authority).
  const filteredItems = useMemo(() => {
    if (!Array.isArray(items)) return items;
    // ALL: pure corpus lens. Return unfiltered (erased rows are
    // already absent from backend response for this role, but honour
    // erased_at defensively).
    const nonErased = items.filter((c) => !c.erased_at);
    if (activeFilter === 'all') return nonErased;
    if (activeFilter === 'awaiting') {
      return nonErased.filter((c) => (
        c.integrity_classification === 'unresolved'
        || c.integrity_classification === 'missing_check'
      ));
    }
    if (activeFilter === 'excluded') {
      return nonErased.filter((c) => c.integrity_classification === 'excluded');
    }
    if (activeFilter === 'eligible') {
      // RC EMPLOYER.WORKFLOW.P0 §Correction 1 (Founder-approved
      // 2026-02-27): ELIGIBLE FOR ASSESSMENT is the progressive
      // workflow action lane — integrity-eligible AND no
      // authoritative completed evaluation. The underlying
      // admissibility partition (`n_eligible`) is preserved intact;
      // this narrowing lives ONLY in the workflow view.
      return nonErased.filter((c) => (
        (
          c.integrity_classification === 'clear'
          || c.integrity_classification === 'included'
          || c.integrity_classification === 'grandfathered_legacy'
        )
        && !c.evaluation_id
      ));
    }
    if (NDOR_KEYS.has(activeFilter)) {
      return nonErased.filter((c) => (
        c.evaluation_status === 'complete'
        && c.governance_verdict === activeFilter
        && !c.authoritative_evaluation_invalid
      ));
    }
    return nonErased;
  }, [items, activeFilter]);

  // Chip counts sourced EXCLUSIVELY from the backend reconciliation
  // envelope so summary and chips can never disagree.
  //
  // RC EMPLOYER.WORKFLOW.P0 §Correction 1: the `eligible` chip count
  // is the progressive-workflow subset (`n_eligible_awaiting_first_assessment`)
  // rather than the full admissibility count (`n_eligible`). The
  // Workflow Summary strip continues to report both so the underlying
  // admissibility partition remains visible.
  const chipCounts = useMemo(() => ({
    all:       reconciliation?.n_all              ?? 0,
    awaiting:  reconciliation?.n_awaiting_integrity ?? 0,
    excluded:  reconciliation?.n_excluded         ?? 0,
    eligible:  reconciliation?.n_eligible_awaiting_first_assessment ?? 0,
    shortlist: reconciliation?.n_shortlist        ?? 0,
    review:    reconciliation?.n_review           ?? 0,
    reject:    reconciliation?.n_reject           ?? 0,
  }), [reconciliation]);

  return (
    <Card className="border-ndor-edge bg-ndor-surface" data-testid="role-candidates-panel">
      <CardHeader className="pb-3 flex flex-row items-center justify-between px-6 pt-6">
        <CardTitle className="text-base font-semibold text-ndor-ink flex items-center gap-2">
          <FileText className="h-4 w-4 text-slate-300" />
          Candidates &amp; Dossiers
        </CardTitle>
        <Button
          variant="ghost"
          size="sm"
          onClick={load}
          aria-label="Refresh candidates"
          data-testid="role-candidates-refresh"
          className="h-8 w-8 p-0"
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-5 px-6 pb-6">
        {/* RC 1.62 Phase B — upload affordances (mode toggle, file
            input, bulk panel mount) live in RoleUploadPanel under the
            "Upload Dossiers" parent tab. */}

        {/* RC 1.62 Role Lifecycle Gate Hardening — calm banner
            surfaces when the role is past `active`. Evaluate /
            Re-evaluate buttons are also individually disabled so
            the banner is reinforcement, not the only gate. */}
        {roleLifecycleBlocksEvaluate && Array.isArray(items) && items.length > 0 && (
          <div
            className="text-xs rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-amber-200"
            data-testid="role-candidates-lifecycle-banner"
          >
            New evaluations are disabled while this role is{' '}
            <code className="font-mono">{_roleStatus || 'not active'}</code>.
            Existing verdicts, governance packs, and reviewer triage
            remain available.
          </div>
        )}

        {error && (
          <div className="text-xs text-rose-400 flex items-start gap-2" data-testid="role-candidates-error">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {items === null && (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-4 w-4 animate-spin text-slate-300" />
          </div>
        )}

        {Array.isArray(items) && items.length === 0 && (
          <div
            className="text-center py-8 px-4 rounded-md border border-dashed border-ndor-edge bg-ndor-bg/40 space-y-2"
            data-testid="role-candidates-empty"
          >
            <p className="text-sm text-ndor-ink font-medium">No candidates yet.</p>
            <p className="text-xs text-slate-200 leading-relaxed max-w-md mx-auto">
              Use the <strong>Upload Dossiers</strong> tab to add your first
              Candidate Dossier. Each dossier is one PDF or DOCX file
              containing the candidate&rsquo;s CV (required), plus any cover
              letter, supporting statement or notes you have on file.
            </p>
          </div>
        )}

        {/* RC EMPLOYER.WORKFLOW.P0 — Workflow Summary + two-group
            filter bar. Rendered once at least one dossier exists. */}
        {Array.isArray(items) && items.length > 0 && (
          <>
            <RoleWorkflowSummary items={items} reconciliation={reconciliation} />

            {/* Group A — Dossiers & Admissibility */}
            <div
              className="space-y-1.5"
              data-testid="role-candidates-filter-group-admissibility"
            >
              <p className="text-[10px] uppercase tracking-[0.18em] text-ndor-ink-secondary font-medium">
                Dossiers &amp; admissibility
              </p>
              <div
                className="flex items-center gap-1.5 flex-wrap"
                role="tablist"
                aria-label="Dossiers and admissibility views"
              >
                {FILTER_GROUP_ADMISSIBILITY.map((f) => {
                  const isActive = activeFilter === f.key;
                  const count = chipCounts[f.key];
                  return (
                    <button
                      key={f.key}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setActiveFilter(f.key)}
                      className={`text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${
                        isActive
                          ? 'border-ndor-ink bg-ndor-ink text-ndor-surface font-semibold'
                          : 'border-ndor-edge bg-ndor-bg/40 text-ndor-ink-secondary hover:text-ndor-ink hover:border-ndor-ink/40'
                      }`}
                      data-testid={`role-candidates-filter-${f.key}`}
                      data-active={isActive ? 'true' : 'false'}
                    >
                      {f.label} ({count})
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Group B — NDOR Evaluation Results */}
            <div
              className="space-y-1.5"
              data-testid="role-candidates-filter-group-ndor-results"
            >
              <p className="text-[10px] uppercase tracking-[0.18em] text-ndor-ink-secondary font-medium">
                NDOR evaluation results
              </p>
              <div
                className="flex items-center gap-1.5 flex-wrap"
                role="tablist"
                aria-label="NDOR completed-evaluation result views"
              >
                {FILTER_GROUP_NDOR.map((f) => {
                  const isActive = activeFilter === f.key;
                  const count = chipCounts[f.key];
                  const activeTone =
                    f.key === 'shortlist' ? 'border-emerald-500/60 bg-emerald-500/20 text-emerald-100'
                    : f.key === 'review'  ? 'border-amber-500/60 bg-amber-500/20 text-amber-100'
                    : /* reject */          'border-rose-500/60 bg-rose-500/20 text-rose-100';
                  return (
                    <button
                      key={f.key}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setActiveFilter(f.key)}
                      className={`text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${
                        isActive
                          ? `${activeTone} font-semibold`
                          : 'border-ndor-edge bg-ndor-bg/40 text-ndor-ink-secondary hover:text-ndor-ink hover:border-ndor-ink/40'
                      }`}
                      data-testid={`role-candidates-filter-${f.key}`}
                      data-active={isActive ? 'true' : 'false'}
                    >
                      {f.label} ({count})
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Feb 2026 workflow correction — Eligible-for-evaluation
                capacity banner + bulk-selection surface. Rendered only
                inside the ELIGIBLE FOR ASSESSMENT view (Founder
                doctrine: first assessment lives only here). */}
            {activeFilter === 'eligible' && (() => {
              // A row is *actionable* in the Eligible for Assessment
              // view when it is integrity-eligible AND has not yet been
              // assessed. Under §Correction 1 the view no longer
              // contains already-assessed rows, so filteredItems is
              // already the actionable set — but we keep the explicit
              // filter as a defence-in-depth guard.
              // A row is *actionable* in the Eligible view when it is
              // integrity-eligible AND has not already been evaluated.
              // Already-evaluated eligible rows remain visible (they
              // carry their verdict for reference and remain
              // available for re-evaluation via the per-row control)
              // but must not be re-dispatched via the bulk-selection
              // path — the backend rejects them (`already_done`) and
              // it is misleading to let the operator tick their
              // checkbox.
              const unevaluatedEligible = (filteredItems || []).filter(
                (c) => c.evaluation_status !== 'complete',
              );
              const unevaluatedEligibleIds = new Set(unevaluatedEligible.map((c) => c.id));
              const unevaluatedCount = unevaluatedEligible.length;
              const monthlyCap   = usage?.monthly_cap ?? null;
              const monthlyUsed  = usage?.monthly_used ?? 0;
              const headroom = (monthlyCap == null)
                ? Number.POSITIVE_INFINITY
                : Math.max(0, monthlyCap - monthlyUsed);
              const selectionCap = Math.min(unevaluatedCount, headroom);
              const selectionCapDisplay = (headroom === Number.POSITIVE_INFINITY)
                ? '∞'
                : String(headroom);
              const availableToSelect = selectionCap;
              const selectedCount = selected.size;
              const canBulkEvaluate = selectedCount > 0
                && !bulkEvalBusy
                && !roleLifecycleBlocksEvaluate;

              const onSelectAllUpToCap = () => {
                const next = new Set();
                for (const c of unevaluatedEligible) {
                  if (next.size >= availableToSelect) break;
                  next.add(c.id);
                }
                setSelected(next);
              };

              return (
                <div
                  className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 space-y-3"
                  data-testid="eligible-view-panel"
                >
                  <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
                    <p className="text-sm font-semibold text-emerald-100"
                       data-testid="eligible-view-headline">
                      Eligible for assessment
                    </p>
                    <p className="text-xs text-emerald-200/90">
                      <span className="text-emerald-100 font-semibold"
                            data-testid="eligible-count">
                        {unevaluatedCount}
                      </span>{' '}
                      dossier{unevaluatedCount === 1 ? '' : 's'} awaiting first assessment
                    </p>
                    <p className="text-xs text-emerald-200/90">
                      <span className="text-emerald-100 font-semibold"
                            data-testid="eligible-available-count">
                        {selectionCapDisplay}
                      </span>{' '}
                      evaluation{headroom === 1 ? '' : 's'} available
                      {monthlyCap != null && (
                        <>
                          {' '}
                          ({monthlyUsed} of {monthlyCap} used
                          {usage?.period_ends_at
                            ? `, resets ${new Date(usage.period_ends_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}`
                            : ''})
                        </>
                      )}
                    </p>
                    <p className="text-xs text-emerald-200/90">
                      <span className="text-emerald-100 font-semibold"
                            data-testid="eligible-selected-count">
                        {selectedCount}
                      </span>{' '}
                      selected
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      size="sm"
                      className="h-8 text-xs uppercase tracking-wider bg-emerald-500/20 border border-emerald-500/50 text-emerald-100 hover:bg-emerald-500/30 disabled:opacity-40"
                      disabled={!canBulkEvaluate}
                      onClick={onBulkEvaluate}
                      data-testid="eligible-bulk-evaluate-btn"
                    >
                      {bulkEvalBusy
                        ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />Dispatching…</>
                        : `Evaluate selected (${selectedCount})`}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 text-xs uppercase tracking-wider border-emerald-500/40 text-emerald-100 hover:bg-emerald-500/10 disabled:opacity-40"
                      disabled={availableToSelect === 0 || bulkEvalBusy}
                      onClick={onSelectAllUpToCap}
                      data-testid="eligible-select-up-to-cap-btn"
                      title="Selects up to the available evaluation capacity in visible order. No AI/scoring/ranking decides which dossiers consume scarce capacity."
                    >
                      Select up to {selectionCapDisplay}
                    </Button>
                    {selectedCount > 0 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 text-xs uppercase tracking-wider text-emerald-200 hover:text-emerald-100"
                        onClick={() => setSelected(new Set())}
                        disabled={bulkEvalBusy}
                        data-testid="eligible-clear-selection-btn"
                      >
                        Clear selection
                      </Button>
                    )}
                  </div>
                  {selectedCount > selectionCap && (
                    <p className="text-[11px] text-amber-200"
                       data-testid="eligible-selection-over-cap">
                      Your selection ({selectedCount}) exceeds the current
                      evaluation capacity ({selectionCapDisplay}). Reduce
                      your selection or upgrade your workspace.
                    </p>
                  )}
                  {/* Expose the actionable-id set on the DOM for
                      per-row rendering below. */}
                  <span
                    className="hidden"
                    data-testid="eligible-unevaluated-ids"
                    data-ids={Array.from(unevaluatedEligibleIds).join(',')}
                  />
                </div>
              );
            })()}
          </>
        )}

        {/* RC ENTITLEMENT.INTEGRITY.FIX + RC EMPLOYER.OVERAGE.REMOVAL
            — institutional limit-reached banner. No overage path. */}
        {usage && usage.limit_reached && (
          <div
            className="rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm leading-relaxed text-amber-200"
            role="status"
            data-testid="role-candidates-limit-reached-banner"
          >
            <p className="font-medium text-amber-100">
              Monthly evaluation allowance reached.
            </p>
            <p className="mt-1 text-amber-200/90">
              Your {usage.plan_tier ? `${usage.plan_tier.charAt(0).toUpperCase()}${usage.plan_tier.slice(1)}` : 'current'} workspace has used {usage.monthly_used} of {usage.monthly_cap} evaluations for the current billing period.
            </p>
            <p className="mt-1 text-amber-200/90">
              {usage.period_ends_at ? (
                <>Resets on {new Date(usage.period_ends_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}.</>
              ) : (
                <>Resets at the next billing period.</>
              )}
              {' '}Upgrade your workspace or wait until reset.
            </p>
            <Link
              to="/employer/account?tab=plans"
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-amber-100 hover:text-amber-50 underline-offset-4 hover:underline"
              data-testid="role-candidates-view-plans-link"
            >
              View plans
            </Link>
          </div>
        )}

        {Array.isArray(filteredItems)
         && filteredItems.length === 0
         && Array.isArray(items) && items.length > 0 && (
          <div
            className="text-center py-6 px-4 rounded-md border border-dashed border-ndor-edge bg-ndor-bg/30"
            data-testid="role-candidates-filter-empty"
          >
            <p className="text-xs text-ndor-ink-secondary">
              No dossiers match the current filter.
            </p>
          </div>
        )}

        {Array.isArray(filteredItems) && filteredItems.length > 0 && (
          <div
            className="max-h-[560px] overflow-y-auto pr-1 -mr-1"
            data-testid="role-candidates-scroll-region"
          >
            <ul className="divide-y divide-ndor-edge" data-testid="role-candidates-list">
            {filteredItems.map((c) => (
              <li
                key={c.id}
                id={`candidate-${c.id}`}
                className="py-5 first:pt-2"
                data-testid="role-candidate-row"
                data-candidate-id={c.id}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1 flex items-start gap-3">
                    {activeFilter === 'eligible' && (
                      <Checkbox
                        className="mt-1 h-4 w-4 border-emerald-500/50 data-[state=checked]:bg-emerald-500 data-[state=checked]:text-emerald-950 disabled:opacity-40"
                        checked={selected.has(c.id)}
                        onCheckedChange={(v) => {
                          setSelected((prev) => {
                            const next = new Set(prev);
                            if (v) next.add(c.id); else next.delete(c.id);
                            return next;
                          });
                        }}
                        disabled={
                          bulkEvalBusy
                          || roleLifecycleBlocksEvaluate
                          || c.evaluation_status === 'complete'
                        }
                        aria-label={
                          c.evaluation_status === 'complete'
                            ? `${c.original_filename} — already evaluated`
                            : `Select ${c.original_filename} for evaluation`
                        }
                        title={
                          c.evaluation_status === 'complete'
                            ? 'This dossier has already been evaluated. Re-evaluate is available via the per-row action.'
                            : undefined
                        }
                        data-testid="eligible-row-checkbox"
                        data-candidate-id={c.id}
                        data-evaluated={c.evaluation_status === 'complete' ? 'true' : 'false'}
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-ndor-ink truncate" title={c.original_filename}>
                        {c.original_filename}
                      </p>
                      <div className="mt-2 flex items-center gap-2 flex-wrap text-xs text-slate-300">
                        <span>{relativeTime(c.created_at)}</span>
                        <span aria-hidden="true">·</span>
                        <span>{formatBytes(c.file_size_bytes)}</span>
                        <span aria-hidden="true">·</span>
                        <span className="font-mono-ui">
                          {String(c.cv_sha256 || '').slice(0, 8)}
                        </span>
                        <span
                          className="text-xs uppercase tracking-wider px-2 py-0.5 rounded border border-slate-500/40 bg-slate-500/10 text-slate-200"
                          title="Candidate Dossier — CV (required) plus optional cover letter, supporting statement, candidate notes"
                          data-testid="role-candidate-dossier-pill"
                        >
                          Dossier
                        </span>
                      </div>
                      {c.parse_status === 'parse_failed' && c.parse_error_code && (
                        <p className="mt-2 text-xs text-rose-300">
                          {c.parse_error_code}
                        </p>
                      )}
                      {/* WP7: integrity badge + expandable detail. */}
                      <IntegrityRow candidate={c} memberRole={memberRole} onDisposed={() => load()} />
                    </div>
                  </div>
                  <div className="shrink-0 flex flex-col items-end gap-1.5">
                    {statusBadge(c.parse_status)}
                    {/* RC EMPLOYER.WORKFLOW.P0 — NDOR recommendation
                        (from emp_evaluations.governance_verdict) and
                        employer decision (from emp_candidates.
                        governance_status) are visually distinct and
                        may co-exist on the same row. Fit score is
                        annotated inline with the NDOR badge. */}
                    {c.evaluation_status === 'complete'
                     && !c.authoritative_evaluation_invalid
                     && ndorBadge(c.governance_verdict)}
                    {c.evaluation_status === 'complete'
                     && !c.authoritative_evaluation_invalid
                     && typeof c.overall_fit === 'number' && (
                      <span
                        className="text-[10px] uppercase tracking-wider text-ndor-ink-secondary"
                        data-testid="role-candidate-fit-score"
                      >
                        Fit {c.overall_fit}/100
                        {typeof c.confidence_low === 'number' && typeof c.confidence_high === 'number'
                          ? ` · conf ${c.confidence_low}–${c.confidence_high}`
                          : ''}
                      </span>
                    )}
                    {c.evaluation_status === 'complete'
                     && c.authoritative_evaluation_invalid && (
                      <Badge
                        variant="outline"
                        className="border-amber-500/60 bg-amber-500/10 text-amber-200 text-[10px] uppercase tracking-wider"
                        data-testid="role-candidate-invariant-flag"
                      >
                        Recommendation invalid
                      </Badge>
                    )}
                    {c.evaluation_status === 'complete'
                     && decisionBadge(c.governance_status)}
                  </div>
                </div>
                {/* W3 — actions on dedicated second line so the row breathes. */}
                <div className="mt-3 flex items-center justify-end gap-4 flex-wrap">
                  {/* RC EMPLOYER.WORKFLOW.P0 (Founder-approved v2):
                      First-time Assess control renders EXCLUSIVELY in
                      the ELIGIBLE FOR ASSESSMENT lane, and only on
                      rows that have not yet been evaluated. Result
                      views (SHORTLIST / REVIEW / REJECT) never expose
                      the initial Assess CTA. */}
                  {activeFilter === 'eligible'
                   && c.parse_status === 'parsed'
                   && c.evaluation_status !== 'complete' && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 text-xs uppercase tracking-wider focus-visible:ring-2 focus-visible:ring-blue-400"
                      onClick={() => onEvaluate(c.id, false)}
                      disabled={Boolean(evalBusy[c.id]) || Boolean(usage && usage.limit_reached) || roleLifecycleBlocksEvaluate}
                      title={roleLifecycleBlocksEvaluate
                        ? 'Evaluations only run on active roles. Publish the role or wait until it leaves the closing/closed state.'
                        : usage && usage.limit_reached
                          ? `Monthly allowance reached (${usage.monthly_used} of ${usage.monthly_cap}). Resets ${usage.period_ends_at ? new Date(usage.period_ends_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'next period'}.`
                          : undefined}
                      data-testid="role-candidate-evaluate-btn"
                      data-limit-reached={usage && usage.limit_reached ? 'true' : 'false'}
                      data-lifecycle-blocked={roleLifecycleBlocksEvaluate ? 'true' : 'false'}
                    >
                      {evalBusy[c.id] ? (
                        <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />Assessing…</>
                      ) : (
                        'Assess'
                      )}
                    </Button>
                  )}
                  {c.evaluation_status === 'complete' && (
                    <button
                      type="button"
                      onClick={() => setExpanded((p) => ({ ...p, [c.id]: !p[c.id] }))}
                      className="text-xs text-slate-200 hover:text-ndor-ink underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded font-medium"
                      data-testid="role-candidate-verdict-toggle"
                      data-expanded={expanded[c.id] ? 'true' : 'false'}
                    >
                      {expanded[c.id] ? 'Hide verdict details' : 'Why this verdict?'}
                    </button>
                  )}
                  {/* RC 1.62 M146 — imported-candidate reviewer routing
                      affordance. Mirrors the surfaced-candidate Route
                      button on `RoleSurfacedCandidatesPanel`; same
                      component, different identity axis. Rendered only
                      when the candidate is evaluated (so the reviewer
                      has substantive evidence to score). */}
                  {c.evaluation_status === 'complete' && (
                    <RouteToReviewersButton
                      empCandidateId={c.id}
                      accountId={accountId}
                      roleId={roleId}
                      activeReviewerCount={activeReviewerCount}
                    />
                  )}
                  {/* RC EMPLOYER.WORKFLOW.P0 §Correction 1 + §
                      Correction 3: Re-evaluate is an explicit
                      exceptional action, available EXCLUSIVELY in the
                      three NDOR result views. Removed from ELIGIBLE
                      FOR ASSESSMENT because that lane no longer
                      contains already-assessed rows under the
                      corrected progressive-workflow semantic. */}
                  {c.evaluation_status === 'complete'
                   && NDOR_KEYS.has(activeFilter) && (
                    <button
                      type="button"
                      onClick={() => setReevalTarget({ id: c.id, filename: c.original_filename })}
                      disabled={Boolean(evalBusy[c.id]) || Boolean(usage && usage.limit_reached) || roleLifecycleBlocksEvaluate}
                      title={roleLifecycleBlocksEvaluate
                        ? 'Re-evaluation only runs on active roles.'
                        : usage && usage.limit_reached
                          ? `Monthly allowance reached (${usage.monthly_used} of ${usage.monthly_cap}). Resets ${usage.period_ends_at ? new Date(usage.period_ends_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'next period'}.`
                          : 'Dispatch a new evaluation for this candidate. The current NDOR recommendation remains displayed until the new evaluation completes successfully.'}
                      className="text-xs text-slate-300 hover:text-ndor-ink underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
                      data-testid="role-candidate-reevaluate-btn"
                      data-limit-reached={usage && usage.limit_reached ? 'true' : 'false'}
                      data-lifecycle-blocked={roleLifecycleBlocksEvaluate ? 'true' : 'false'}
                    >
                      {evalBusy[c.id] ? (
                        <><Loader2 className="inline mr-1 h-3 w-3 animate-spin" />Re-evaluating…</>
                      ) : 'Re-evaluate'}
                    </button>
                  )}
                  {/* RC EMPLOYER.WORKFLOW.P0 — manual verdict override. */}
                  {c.evaluation_status === 'complete' && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          disabled={Boolean(overrideBusy[c.id])}
                          className="text-xs text-slate-300 hover:text-ndor-ink underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded inline-flex items-center gap-1 disabled:opacity-50"
                          data-testid="role-candidate-override-trigger"
                        >
                          {overrideBusy[c.id] ? (
                            <><Loader2 className="h-3 w-3 animate-spin" />Updating…</>
                          ) : (
                            <>Override <ChevronDown className="h-3 w-3" /></>
                          )}
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        className="bg-ndor-surface border-ndor-edge"
                        data-testid="role-candidate-override-menu"
                      >
                        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-ndor-ink-secondary">
                          Move verdict
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        {OVERRIDE_OPTIONS.map((opt) => (
                          <DropdownMenuItem
                            key={opt.key}
                            disabled={c.governance_status === opt.key}
                            onSelect={() => onOverrideVerdict(c.id, opt.key)}
                            className="text-xs text-ndor-ink hover:bg-ndor-surface-alt cursor-pointer"
                            data-testid={`role-candidate-override-${opt.key}`}
                          >
                            {opt.label}
                            {c.governance_status === opt.key ? (
                              <span className="ml-2 text-[9px] uppercase text-ndor-ink-muted">
                                current
                              </span>
                            ) : null}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
                {c.evaluation_status === 'complete' && (
                  <CandidateVerdictExplainer
                    candidateId={c.id}
                    open={Boolean(expanded[c.id])}
                    role={role}
                  />
                )}
              </li>
            ))}
          </ul>
          </div>
        )}

        {Array.isArray(items) && items.length > 0 && (
          <GovernancePackPanel roleId={roleId} items={items} />
        )}

        {Array.isArray(items) && items.length > 0 && (
          <p
            className="text-xs text-slate-300 text-center pt-3 border-t border-ndor-edge"
            data-testid="role-candidates-next-action"
          >
            First assessment is available only from{' '}
            <span className="font-medium text-ndor-ink">Eligible for assessment</span>.
            NDOR recommendations (Shortlist / Review / Reject) are shown in the corresponding result views.
            Use <span className="font-medium text-ndor-ink">Override</span> on a row to record an employer decision.
          </p>
        )}
      </CardContent>
      {/* RC EMPLOYER.WORKFLOW.P0 (Founder-approved v2 §Correction 3) —
          Re-evaluate confirmation. The current authoritative
          recommendation continues to display in the result view
          while the new evaluation runs; only successful completion
          of the new evaluation displaces it. Historical rows on
          emp_evaluations remain immutable. */}
      <AlertDialog
        open={reevalTarget !== null}
        onOpenChange={(v) => { if (!v) setReevalTarget(null); }}
      >
        <AlertDialogContent
          className="bg-ndor-surface border-ndor-edge"
          data-testid="role-candidate-reevaluate-confirm"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-ndor-ink">Re-evaluate this dossier?</AlertDialogTitle>
            <AlertDialogDescription className="text-ndor-ink-secondary space-y-2">
              <span className="block">
                A new NDOR evaluation will be dispatched for
                <span className="font-medium text-ndor-ink"> {reevalTarget?.filename}</span>.
                It will consume one evaluation from your monthly allowance.
              </span>
              <span className="block">
                The current authoritative recommendation and result view remain in place until
                the new evaluation completes successfully. Historical evaluations are preserved and
                never overwritten.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="border-ndor-edge text-ndor-ink-secondary hover:bg-ndor-surface-alt"
              data-testid="role-candidate-reevaluate-cancel"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-slate-500/30 border border-slate-500/60 text-ndor-ink hover:bg-slate-500/40"
              onClick={() => {
                const id = reevalTarget?.id;
                setReevalTarget(null);
                if (id) onEvaluate(id, true);
              }}
              data-testid="role-candidate-reevaluate-confirm-btn"
            >
              Re-evaluate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Phase D F3 — Governance Decision Surface.
          Opens when the operator selects a non-'open' override.
          Backend remains authoritative on the ≥100-char rationale
          requirement; the surface renders a live counter so the
          operator is never blindsided. Uses existing template
          components — no visual redesign. */}
      <AlertDialog
        open={decisionModal !== null}
        onOpenChange={(v) => { if (!v) { setDecisionModal(null); setDecisionNote(''); } }}
      >
        <AlertDialogContent
          className="bg-ndor-surface border-ndor-edge max-w-lg"
          data-testid="governance-decision-surface"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-ndor-ink">
              Record employer decision
            </AlertDialogTitle>
            <AlertDialogDescription className="text-ndor-ink-secondary space-y-2">
              <span className="block">
                Recording <span className="font-medium text-ndor-ink">
                {decisionModal?.target}</span> for
                <span className="font-medium text-ndor-ink"> {decisionModal?.candidateName}</span>.
              </span>
              {decisionModal?.ndorVerdict && (
                <span className="block text-xs">
                  NDOR recommendation for this candidate:
                  <span className="font-medium text-ndor-ink ml-1">
                    {decisionModal.ndorVerdict}
                  </span>.
                </span>
              )}
              {decisionModal && isMaterialDivergence(decisionModal.ndorVerdict, decisionModal.target) && (
                <span
                  className="block text-xs text-amber-300"
                  data-testid="decision-divergence-hint"
                >
                  This decision materially diverges from NDOR&rsquo;s recommendation.
                  A rationale of at least 100 characters is required.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label
                className="block text-xs uppercase tracking-wider text-ndor-ink-secondary mb-1"
                htmlFor="decision-reason-select"
              >Decision reason</label>
              <select
                id="decision-reason-select"
                data-testid="decision-reason-select"
                className="w-full bg-ndor-surface-alt border border-ndor-edge rounded px-2 py-1.5 text-sm text-ndor-ink"
                value={decisionReason}
                onChange={(e) => setDecisionReason(e.target.value)}
              >
                <option value="ndor_recommendation_accepted">NDOR recommendation accepted</option>
                <option value="ndor_recommendation_overruled">NDOR recommendation overruled</option>
                <option value="reviewer_evidence_accepted">Reviewer evidence accepted</option>
                <option value="post_interview">Post-interview</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label
                className="block text-xs uppercase tracking-wider text-ndor-ink-secondary mb-1"
                htmlFor="decision-note-textarea"
              >Rationale</label>
              <textarea
                id="decision-note-textarea"
                data-testid="decision-note-textarea"
                className="w-full h-28 bg-ndor-surface-alt border border-ndor-edge rounded px-2 py-1.5 text-sm text-ndor-ink resize-none"
                value={decisionNote}
                onChange={(e) => setDecisionNote(e.target.value.slice(0, 2000))}
                placeholder="Explain the decision. Required for divergent decisions (min 100 characters)."
              />
              <div
                className="mt-1 flex items-center justify-between text-xs"
                data-testid="decision-note-counter"
              >
                {(() => {
                  const len = decisionNote.trim().length;
                  const divergent = decisionModal
                    ? isMaterialDivergence(decisionModal.ndorVerdict, decisionModal.target)
                    : false;
                  const min = divergent ? 100 : 0;
                  const cls = divergent && len < min
                    ? 'text-amber-300' : 'text-ndor-ink-secondary';
                  return (
                    <>
                      <span className={cls}>
                        {len}/{divergent ? '≥100' : '2000'} characters
                      </span>
                      {divergent && (
                        <span className={cls}>
                          {Math.max(0, min - len)} more required
                        </span>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="border-ndor-edge text-ndor-ink-secondary hover:bg-ndor-surface-alt"
              data-testid="decision-surface-cancel"
              disabled={decisionSaving}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-emerald-500/30 border border-emerald-500/60 text-ndor-ink hover:bg-emerald-500/40"
              onClick={(e) => { e.preventDefault(); submitDecision(); }}
              data-testid="decision-surface-submit"
              disabled={
                decisionSaving
                || (decisionModal
                    && isMaterialDivergence(decisionModal.ndorVerdict, decisionModal.target)
                    && decisionNote.trim().length < 100)
              }
            >
              Record decision
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* RC EMPLOYER.OVERAGE.REMOVAL — EvalChargeConfirmDialog removed.
          Beyond-cap is a hard 402 surfaced via toast + the in-page
          limit-reached banner above; no overage acceptance UI. */}
    </Card>
  );
}
